import AccountService, { userLimitations } from "./AccountService";
import firebase from "firebase/compat/app";

import { observable, toJS } from "mobx";
import { v4 as uuidv4 } from "uuid";

export type CreateFileResult =
  | "INVALID_NAME"
  | "INVALID_USER"
  | "UNKNOWN_ERROR"
  | "SUCCESS";
export type DeleteFileResult =
  | "INVALID_ID"
  | "INVALID_USER"
  | "UNKNOWN_ERROR"
  | "SUCCESS";
export type FileResult = "UNKNOWN_ERROR" | string;

export type FileMetadata = {
  fileId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  clinic: string;
  defaultFile: boolean;
};

export type QAResponse = {
  q: string;
  a: string;
  filesNames?: any[];
  uploadedFilesLinks?: any[];
};

export type NarrativeMetadata = {
  id?: string;
  flowId: string;
  flowName: string;
  date?: string;
  userName: string;
  narrative: string;
  responses: QAResponse[];
  clinic: string;
};

export default class FileService {
  private _accountService: AccountService;
  private _db: firebase.firestore.Firestore;
  private _currentNarrativeId: string = "";

  private _chatbotColor: string = "";

  public get chatbotColor(): string {
    return this._chatbotColor;
  }

  public setChatbotColor(color: string) {
    this._chatbotColor = color;
  }

  @observable
  private _files = new Array<FileMetadata>();

  @observable
  private _narratives = new Array<NarrativeMetadata>();

  public get files(): Array<FileMetadata> {
    return this._files;
  }

  public get narratives(): Array<NarrativeMetadata> {
    return this._narratives;
  }
  public get currentNarrativeId(): string {
    return this._currentNarrativeId;
  }

  public setCurrentNarrativeId(id: string) {
    this._currentNarrativeId = id;
  }

  constructor(account: AccountService) {
    this._accountService = account;
  }
  async initialize() {
    this._db = firebase.firestore();
  }

  async refreshFileList() {
    try {
      var user = this._accountService.user;
      if (user == null) {
        return;
      }
      var collectionId = `file-desc-${user.uid}`;
      var result = await this._db
        .collection(collectionId)
        .orderBy("createdAt", "desc")
        .get();

      this._files = new Array<FileMetadata>();

      result.docs.forEach((doc) => {
        var data = doc.data();
        this._files.push({
          fileId: data.fileId,
          name: data.name,
          createdAt: data.currentDate,
          updatedAt: data.currentDate,
          clinic: data.clinic,
          defaultFile: data.defaultFile ? data.defaultFile : false,
        });
      });

      let userPlan = userLimitations.find(
        (plan) => this._accountService.userData.userType === plan.userType
      );
      let plan = this._accountService.IsPlanExpired();

      if (userPlan.workflow <= this._files.length && userPlan.workflow !== -1) {
        plan[1].limit = true;
        plan[1].count = userPlan.workflow;
      } else {
        plan[1].limit = false;
        plan[1].count = -1;
      }
      await this._accountService.setPlanExpiry(plan);
    } catch (error) {
      console.log(error);
    }
  }

  async refreshNarrativeList() {
    try {
      var user = this._accountService.user;
      if (user == null) {
        return;
      }

      var collectionId = `narrative-data-${user.uid}`;

      var result = await this._db
        .collection(collectionId)
        .orderBy("date", "desc")
        .get();

      this._narratives = new Array<NarrativeMetadata>();

      result.docs.forEach((doc) => {
        var data = doc.data();
        this._narratives.push({
          id: data.id,
          flowId: data.flowId,
          flowName: data.flowName,
          date: data.date,
          userName: data.userName,
          narrative: data.narrative,
          responses: data.responses,
          clinic: data.clinic,
        });
      });

      let userPlan = userLimitations.find(
        (plan) => this._accountService.userData.userType === plan.userType
      );
      let plan = this._accountService.IsPlanExpired();
      if (
        userPlan.narrative <= this._narratives.length &&
        userPlan.narrative !== -1
      ) {
        plan[2].limit = true;
        plan[2].count = this._narratives.length - userPlan.narrative;
        this._narratives = this._narratives.splice(0, userPlan.narrative);
      } else {
        plan[2].limit = false;
        plan[2].count = -1;
      }
      await this._accountService.setPlanExpiry(plan);

      // console.log(toJS(this._narratives));
    } catch (error) {
      console.log(error);
    }
  }

  async deleteFile(fileId: string): Promise<DeleteFileResult> {
    if (fileId == null || fileId.length == 0) {
      return "INVALID_ID";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "INVALID_USER";
    }
    var collectionId = `file-desc-${user.uid}`;

    try {
      await this._db.collection(collectionId).doc(fileId).delete();
      var deleteDataResult = await this.deleteFileData(fileId);

      if (deleteDataResult != "SUCCESS") {
        return deleteDataResult;
      }

      this._files = this._files.filter((file) => file.fileId != fileId);
      await this.refreshFileList();
      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async createFile(
    name: string,
    defaultFile?: boolean
  ): Promise<CreateFileResult> {
    if (name.length < 4) {
      return "INVALID_NAME";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "INVALID_USER";
    }
    var collectionId = `file-desc-${user.uid}`;
    var fileId = uuidv4();
    var currentDate = new Date().toString();
    var currentClinic = this._accountService.currentClinic;

    if (currentClinic == "") {
      return "INVALID_USER";
    }
    const linkUrl = `https://app.flowcharts.ai/questionnaire/${user.uid}/${currentClinic}/${fileId}`;

    try {
      var fileMeta = {
        fileId: fileId,
        name: name,
        createdAt: currentDate,
        updatedAt: currentDate,
        clinic: currentClinic,
        defaultFile: defaultFile ? defaultFile : false,
      };
      await this._db.collection(collectionId).doc(fileId).set(fileMeta);
      await this.saveFileData(fileId, "");
      this._files.unshift(fileMeta);
      await this._db.collection("urls").doc(fileId).set({
        longUrl: linkUrl,
      });
      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async createDuplicateFile(
    name: string,
    originFileId: string,
    namesList: string[]
  ): Promise<CreateFileResult> {
    if (name.length < 4) {
      return "INVALID_NAME";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "INVALID_USER";
    }
    var collectionId = `file-desc-${user.uid}`;
    var fileId = uuidv4();
    var currentDate = new Date().toString();
    const fileData = await this.loadFileData(originFileId);
    const count = namesList.filter((fileName) =>
      fileName.includes(`${name} copy`)
    ).length;

    var currentClinic = this._accountService.currentClinic;

    if (currentClinic == "") {
      return "INVALID_USER";
    }

    const linkUrl = `https://app.flowcharts.ai/questionnaire/${user.uid}/${currentClinic}/${fileId}`;

    try {
      var fileMeta = {
        fileId: fileId,
        name: `${name} ${count === 0 ? "copy" : `copy ${count + 1}`}`,
        createdAt: currentDate,
        updatedAt: currentDate,
        clinic: currentClinic,
        defaultFile: false,
      };
      await this._db.collection(collectionId).doc(fileId).set(fileMeta);
      await this.saveFileData(fileId, fileData);
      this._files.unshift(fileMeta);
      await this._db.collection("urls").doc(fileId).set({
        longUrl: linkUrl,
      });
      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async saveNarrative(data: NarrativeMetadata) {
    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var currentClinic = this._accountService.currentClinic;

    if (currentClinic == "") {
      return "INVALID_USER";
    }

    data.clinic = currentClinic;

    var collectionId = `narrative-data-${user.uid}`;
    var narrativeId = uuidv4();
    data.date = new Date().toString();
    data.id = narrativeId;

    try {
      await this._db.collection(collectionId).doc(narrativeId).set(data);
    } catch (err) {
      console.log(err);
      return "UNKNOWN_ERROR";
    }
  }
  async updateNarrative(data: NarrativeMetadata, userId: string) {
    // var user = this._accountService.user;

    if (userId == null) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `narrative-data-${userId}`;

    try {
      await this._db
        .collection(collectionId)
        .doc(this._currentNarrativeId)
        .set(data);
    } catch (err) {
      console.log(err);
      return "UNKNOWN_ERROR";
    }
  }

  async saveFileDataBackup(id: string, fileData: string): Promise<FileResult> {
    if (id == null || id.length == 0 || fileData == null) {
      return "UNKNOWN_ERROR";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var workflowId = `workflow-${user.uid}`;
    const date = `${Date.now()}`;

    try {
      await this._db
        .collection("WorkflowsBackup")
        .doc(workflowId)
        .collection(id)
        .doc(date)
        .set({
          date: new Date().toUTCString(),
          data: fileData,
        });
      return "";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async saveFileData(id: string, fileData: string): Promise<FileResult> {
    if (id == null || id.length == 0 || fileData == null) {
      return "UNKNOWN_ERROR";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `file-data-${user.uid}`;

    try {
      await this._db.collection(collectionId).doc(id).set({
        data: fileData,
      });
      return "";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async deleteFileData(fileId: string): Promise<DeleteFileResult> {
    if (fileId == null || fileId.length == 0) {
      return "INVALID_ID";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "INVALID_USER";
    }
    var collectionId = `file-data-${user.uid}`;

    try {
      await this._db.collection(collectionId).doc(fileId).delete();
      this._files = this._files.filter((file) => file.fileId != fileId);

      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async loadNarrativeData(id: string): Promise<NarrativeMetadata | string> {
    if (id == null || id.length == 0) {
      return "UNKNOWN_ERROR";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `narrative-data-${user.uid}`;

    try {
      var fileValue = await this._db.collection(collectionId).doc(id).get();
      var data: any = fileValue.data();

      if (data == null) {
        return "UNKNOWN_ERROR";
      }
      return data;
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async deleteNarrative(id: string): Promise<DeleteFileResult> {
    if (id == null || id.length == 0) {
      return "INVALID_ID";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "INVALID_USER";
    }
    var collectionId = `narrative-data-${user.uid}`;

    try {
      await this._db.collection(collectionId).doc(id).delete();

      this._narratives = this._narratives.filter((n) => n.id != id);
      await this.refreshNarrativeList();
      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async loadFileNameUnsecure(id: string, userId: string): Promise<FileResult> {
    if (id == null || id.length == 0 || userId == null || userId.length == 0) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `file-desc-${userId}`;

    try {
      var fileValue = await this._db.collection(collectionId).doc(id).get();
      var data = fileValue.data() as any;

      if (data == null) {
        return "UNKNOWN_ERROR";
      }
      return data.name;
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async loadFileDataUnsecure(id: string, userId: string): Promise<FileResult> {
    if (id == null || id.length == 0 || userId == null || userId.length == 0) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `file-data-${userId}`;

    try {
      var fileValue = await this._db.collection(collectionId).doc(id).get();
      var data = fileValue.data() as any;

      if (data == null) {
        return "UNKNOWN_ERROR";
      }

      return data.data;
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async loadFileData(id: string): Promise<FileResult> {
    if (id == null || id.length == 0) {
      return "UNKNOWN_ERROR";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `file-data-${user.uid}`;

    try {
      var fileValue = await this._db.collection(collectionId).doc(id).get();
      var data: any = fileValue.data();

      if (data == null) {
        return "UNKNOWN_ERROR";
      }
      return data.data;
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async loadFileName(id: string): Promise<FileResult> {
    if (id == null || id.length == 0) {
      return "UNKNOWN_ERROR";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `file-desc-${user.uid}`;

    try {
      var fileValue = await this._db.collection(collectionId).doc(id).get();
      var data: any = fileValue.data();

      if (data == null) {
        return "UNKNOWN_ERROR";
      }
      return data.name;
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async updateFileName(id: string, fileName: string): Promise<FileResult> {
    if (id == null || id.length == 0) {
      return "UNKNOWN_ERROR";
    }

    var user = this._accountService.user;

    if (user == null) {
      return "UNKNOWN_ERROR";
    }

    var collectionId = `file-desc-${user.uid}`;
    let newFileNameData = {
      name: fileName,
    };
    try {
      await this._db.collection(collectionId).doc(id).update(newFileNameData);

      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async checkShortenUrl(url: string, fileId: string) {
    const linkUrl = `https://app.flowcharts.ai${url ? url : ""}`;
    try {
      const result = await this._db.collection("urls").doc(fileId).get();
      const docData = result.data();
      if (!docData?.shortUrl) {
        await this._db.collection("urls").doc(fileId).delete();
        await this._db.collection("urls").doc(fileId).set({
          longUrl: linkUrl,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  async getShortenUrl(id: string): Promise<string> {
    try {
      const result = await this._db.collection("urls").doc(id).get();
      const docData = await result.data();
      return docData?.shortUrl;
    } catch (err) {
      console.log(err);
    }
  }

  async getWorkflows(location: string): Promise<any[]> {
    const user = this._accountService.user;
    var collectionId = `file-desc-${user.uid}`;
    if (!location) {
      return [];
    }
    try {
      const result = await this._db
        .collection(collectionId)
        .where("clinic", "==", location)
        .get();
      const workflowsData = result.docs
        .map((doc) => doc.data())
        .map((loc) => {
          return {
            workflowId: loc.fileId,
            workflowName: loc.name,
            locationId: loc.clinic,
          };
        });
      return workflowsData;
    } catch (error) {
      console.log("error", error);
      return [];
    }
  }
}
